/**
 * MessageBubble — renders a single chat message.
 *
 * Handles: own vs. other alignment, edit/delete indicators,
 * reaction display, context menu (edit, delete, pin, react, report).
 */
import React, { useState, useRef, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import DeleteButton from '../DeleteButton/DeleteButton';
import UserAvatar from '../UserAvatar/UserAvatar';
import type { MessageResponse, MessageAttachment } from '../../api/chat';
import { mediaSrc } from '../../api/mediaUrl';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import styles from './Chat.module.css';

interface MessageBubbleProps {
  message: MessageResponse;
  isOwn: boolean;
  onEdit?: (messageId: string) => void;
  onDelete?: (messageId: string) => void;
  onPin?: (messageId: string) => void;
  onReact?: (messageId: string, emoji: string) => void;
  onReport?: (messageId: string) => void;
}

const QUICK_REACTIONS = ['👍', '👎', '❤️', '🔥', '😂', '😮', '😢', '🤔', '✅', '🎉', '👀', '🙏', '💩'];

/** Format bytes into a human-readable size string. */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Check if content type is a previewable image. */
function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

/** Check if content type is a video. */
function isVideo(contentType: string): boolean {
  return contentType.startsWith('video/');
}

/** Get an icon emoji for a file type. */
function fileTypeIcon(contentType: string): string {
  if (contentType.startsWith('image/')) return '🖼️';
  if (contentType.startsWith('video/')) return '🎬';
  if (contentType === 'application/pdf') return '📄';
  return '📎';
}

export default function MessageBubble({
  message,
  isOwn,
  onEdit,
  onDelete,
  onPin,
  onReact,
  onReport,
}: MessageBubbleProps) {
  const { t } = useAppTranslation('chat');
  const [showActions, setShowActions] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<MessageAttachment | null>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = useCallback(() => setShowActions(true), []);
  const handleMouseLeave = useCallback(() => {
    setShowActions(false);
    setShowReactions(false);
  }, []);

  const formatTime = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  if (message.isDeleted) {
    return (
      <div className={`${styles.messageBubble} ${styles.messageDeleted} ${isOwn ? styles.messageOwn : styles.messageOther}`}>
        <span className={styles.deletedText}>{t('conversation.deleted')}</span>
      </div>
    );
  }

  return (
    <div
      id={`msg-${message.id}`}
      className={`${styles.messageRow} ${isOwn ? styles.messageRowOwn : styles.messageRowOther}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ transition: 'background 0.3s' }}
    >
      {/* Avatar (other user only) */}
      {!isOwn && (
        <UserAvatar
          userGuid={message.senderGuid}
          avatarUrl={message.senderProfileImageUrl || undefined}
          userName={message.senderDisplayName || '?'}
          size="xs"
          showPresence
        />
      )}

      <div className={`${styles.messageBubble} ${isOwn ? styles.messageOwn : styles.messageOther}`}>
        {/* Sender name (group chats, other user only) */}
        {!isOwn && message.senderDisplayName && (
          <div className={styles.senderName}>{message.senderDisplayName}</div>
        )}

        {/* Content */}
        {message.content && (
          <div className={styles.messageContent}>{message.content}</div>
        )}

        {/* Attachments */}
        {message.attachments?.length > 0 && (
          <div style={{ marginBlockStart: message.content ? 6 : 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {message.attachments.map((att: MessageAttachment) => {
              const url = mediaSrc(att.fileKey) ?? '';

              if (isImage(att.contentType)) {
                return (
                  <button
                    key={att.id}
                    type="button"
                    onClick={() => setPreviewAttachment(att)}
                    style={{
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'block',
                    }}
                  >
                    <img
                      src={url}
                      alt={att.fileName}
                      style={{
                        maxWidth: 280,
                        maxHeight: 200,
                        borderRadius: 'var(--radius-sm)',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </button>
                );
              }

              if (isVideo(att.contentType)) {
                return (
                  <div key={att.id} style={{ position: 'relative' }}>
                    <video
                      src={url}
                      preload="metadata"
                      style={{
                        maxWidth: 280,
                        maxHeight: 200,
                        borderRadius: 'var(--radius-sm)',
                        display: 'block',
                      }}
                    />
                    {/* Click overlay to open in modal */}
                    <button
                      type="button"
                      onClick={() => setPreviewAttachment(att)}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0,0,0,0.3)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 32,
                        color: '#fff',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.3)';
                      }}
                    >
                      ▶
                    </button>
                  </div>
                );
              }

              // Generic file (PDF, etc.) — click to preview
              return (
                <button
                  key={att.id}
                  type="button"
                  onClick={() => setPreviewAttachment(att)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    background: 'var(--bg-tertiary)',
                    borderRadius: 'var(--radius-sm)',
                    textDecoration: 'none',
                    color: 'var(--text-primary)',
                    fontSize: 12,
                    border: '1px solid var(--border-default)',
                    cursor: 'pointer',
                    textAlign: 'start',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--bg-surface)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--bg-tertiary)';
                  }}
                >
                  <span style={{ fontSize: 20 }}>{fileTypeIcon(att.contentType)}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      display: 'block',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {att.fileName}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                      {formatFileSize(att.fileSizeBytes)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Meta line: time + edited indicator */}
        <div className={styles.messageMeta}>
          <span className={styles.messageTime}>{formatTime(message.createdAt)}</span>
          {message.isEdited && (
            <span className={styles.editedIndicator}>{t('conversation.edited')}</span>
          )}
          {message.isPinned && (
            <span className={styles.pinnedIndicator}>📌</span>
          )}
        </div>

        {/* Reactions bar */}
        {message.reactions.length > 0 && (
          <div className={styles.reactionsBar}>
            {message.reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                className={`${styles.reactionBadge} ${r.currentUserReacted ? styles.reactionActive : ''}`}
                onClick={() => onReact?.(message.id, r.emoji)}
              >
                {r.emoji} {r.count}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Hover action bar */}
      {showActions && (
        <div className={styles.messageActions} ref={actionsRef}>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => setShowReactions((v) => !v)}
            title={t('actions.react')}
          >
            😊
          </button>
          {isOwn && onEdit && (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => onEdit(message.id)}
              title={t('actions.edit')}
            >
              ✏️
            </button>
          )}
          {onDelete && (
            <DeleteButton
              onClick={() => onDelete(message.id)}
              size="sm"
              title={t('actions.delete')}
            />
          )}
          {onPin && (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => onPin(message.id)}
              title={message.isPinned ? t('actions.unpin') : t('actions.pin')}
            >
              📌
            </button>
          )}
          {!isOwn && onReport && (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => onReport(message.id)}
              title={t('actions.report')}
            >
              ⚑
            </button>
          )}
        </div>
      )}

      {/* Quick reaction picker */}
      {showReactions && (
        <div className={styles.reactionPicker}>
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={styles.reactionPickerBtn}
              onClick={() => {
                onReact?.(message.id, emoji);
                setShowReactions(false);
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      {/* Attachment preview modal */}
      {previewAttachment && (
        <AttachmentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );
}
